import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider } from '@mui/material';
import { useGetOne, useNotify, useRedirect, useUpdate } from 'react-admin';
import { Contract } from '../types';
import { getRefreshToken, getToken } from '../authentication/authProvider';
import { preplanToAtNeedPatch } from '../funerals/preneedMode';
import FruitionPrimaryPicker, { FruitionPrimaryChoice } from './FruitionPrimaryPicker';

type Props = {
  open: boolean;
  onClose: () => void;
  contract: Contract;
  region?: string | null;
};

/**
 * Convert for a pre-plan only case (T-172), when the person has died.
 *
 * Unlike the TruStage Fruition dialog this books no partner payment and marks
 * nothing paid: nothing was paid. The pre-need contract becomes an ordinary
 * at-need contract, and staff land on its edit page to review it and send it
 * like any other at-need case.
 */
const PreplanConvertDialog = ({ open, onClose, contract, region }: Props) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [busy, setBusy] = useState(false);
  // T-210: the person has died; the main contact must be a family member.
  const [primaryChoice, setPrimaryChoice] = useState<FruitionPrimaryChoice | null>(null);
  const { data: funeral } = useGetOne(
    'funerals',
    { id: contract.funeral_id },
    { enabled: open && !!contract.funeral_id },
  );

  const convert = async () => {
    if (!primaryChoice) return;
    setBusy(true);
    try {
      // Main contact first: if it fails nothing is converted.
      const primaryRes = await fetch(
        `${process.env.REACT_APP_PAPERMAN_API_URL}contract/fruition-primary/${contract.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getToken()}`,
            'Refresh-Token': await getRefreshToken(),
          },
          body: JSON.stringify(primaryChoice),
        },
      );
      if (!primaryRes.ok) {
        const err = await primaryRes.json().catch(() => ({}));
        notify(err?.message || 'Could not set the new primary contact', { type: 'error' });
        return;
      }
      await update(
        'contracts',
        {
          id: contract.id,
          data: { ...contract, ...preplanToAtNeedPatch(contract as any, region) },
          previousData: contract,
        },
        { returnPromise: true },
      );
      // Same endpoint the contract save button calls: recomputes subtotal,
      // taxes and total at the at-need prices and updates QuickBooks.
      const res = await fetch(
        `${process.env.REACT_APP_PAPERMAN_API_URL}quickbooks/estimates/${contract.id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            'Refresh-Token': await getRefreshToken(),
          },
        },
      );
      notify(
        res.ok
          ? 'Converted to at-need. Review the contract, then send it.'
          : 'Converted to at-need, but the totals did not update in QuickBooks. Save the contract to retry.',
        { type: res.ok ? 'success' : 'warning', multiLine: true },
      );
      onClose();
      redirect('edit', 'contracts', contract.id);
    } catch {
      notify('Could not convert the contract', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Convert to at-need?</DialogTitle>
      <DialogContent className="text-sm space-y-2">
        <p>This case is pre-plan only, so nothing has been paid and there is no TruStage policy.</p>
        <p>
          Contract #{contract.id} becomes an at-need contract at at-need prices. The family then
          signs and pays like any at-need case.
        </p>
        <Divider sx={{ my: 2 }} />
        <FruitionPrimaryPicker funeral={funeral} onChange={setPrimaryChoice} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void convert()} disabled={busy || !primaryChoice}>
          Convert to at-need
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreplanConvertDialog;
